import useSWRInfinite from 'swr/infinite'
import { API_URL } from '@/utils/constants'
import { strictFetcher } from './strict-fetcher'

/// One row of `GET /alkane/asset/:id/holders` — alkanode's ranked balance
/// book, labelled server-side. `holder.kind` distinguishes wallets from
/// CONTRACTS holding the token (a pool's own reserve, the router vault);
/// `label` names contracts and is null for wallets; `icon` carries a
/// registry token's icon when the contract is one. `amount` is base units
/// (8dp) as a decimal string; `pct_of_supply` and `value_usd` are null when
/// supply or price is unknown — never a fabricated zero.
export interface AssetHolder {
	rank: number
	holder: { kind: 'address' | 'contract'; id: string }
	label: string | null
	icon?: string | null
	amount: string
	pct_of_supply: number | null
	value_usd: number | null
}

interface HoldersBody {
	id: string
	rows: AssetHolder[]
	/// The book continues past these rows.
	has_more: boolean
	/// alkanode's full holder count, when it sent one.
	total: number | null
	supply: string | null
}

/// Rows per page — matches the API's MAX_LIMIT so each scroll step is one
/// request against the server's cached 500-deep book.
const PAGE = 50

/// The distinction the table must keep (same contract as useAssetTrades):
/// `unavailable` (upstream down, or an API that predates the endpoint — a
/// 400/404, NOT an empty list) versus a loaded book with zero rows. "Nobody
/// holds this" and "cannot know" are different claims, and the server errors
/// rather than blurring them.
export default function useAssetHolders(assetId: string | null): {
	holders: AssetHolder[] | undefined
	hasMore: boolean
	total: number | null
	unavailable: boolean
	isLoading: boolean
	loadingMore: boolean
	loadMore: () => void
} {
	const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite<HoldersBody>(
		(index, prev) => {
			if (!assetId) {
				return null
			}
			if (prev && (!prev.has_more || prev.rows.length < PAGE)) {
				return null
			}
			return `${API_URL}/alkane/asset/${assetId}/holders?limit=${PAGE}&offset=${index * PAGE}`
		},
		strictFetcher,
		{
			refreshInterval: 120_000,
			revalidateOnFocus: false,
			revalidateFirstPage: true,
			revalidateAll: false,
			keepPreviousData: false
		}
	)
	const pages = data ?? []
	const last = pages[pages.length - 1]
	const hasMore = last ? last.has_more && last.rows.length >= PAGE : false
	return {
		holders: data ? pages.flatMap((p) => p.rows) : undefined,
		hasMore,
		total: pages[0]?.total ?? null,
		unavailable: !!error && !data,
		isLoading,
		loadingMore: isValidating && size > 1,
		loadMore: () => {
			if (hasMore && !isValidating) {
				void setSize((s) => s + 1)
			}
		}
	}
}
